/**
 * Inventory Status Display Component
 *
 * Shows inventory status for SO products including:
 * - Total available quantity across all locations
 * - Location distribution count
 * - Warning indicators for inventory issues
 */

import React from 'react';
import { Badge } from '../../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  MapPin
} from 'lucide-react';

function InventoryStatusDisplay({ product }) {
  if (!product) return null;

  const {
    totalAvailable = 0,
    locationsWithStock = 0,
    quantity_planned = 0,
    inventoryWarning,
    frozen_qty = 0,
    unit = '支'
  } = product;

  // Determine status color and icon
  const getStatusColor = () => {
    if (inventoryWarning === 'no_inventory' || inventoryWarning === 'no_stock') {
      return 'destructive';
    }
    if (inventoryWarning === 'insufficient_total') {
      return 'secondary';
    }
    if (totalAvailable >= quantity_planned) {
      return 'default';
    }
    if (totalAvailable >= quantity_planned * 0.5) {
      return 'secondary';
    }
    return 'destructive';
  };

  const getStatusIcon = () => {
    const color = getStatusColor();
    if (color === 'default') return <CheckCircle className="h-4 w-4 text-success" />;
    if (color === 'secondary') return <AlertTriangle className="h-4 w-4 text-warning" />;
    return <XCircle className="h-4 w-4 text-destructive" />;
  };

  const getStatusText = () => {
    if (inventoryWarning === 'no_inventory') {
      return '无库存记录';
    }
    if (inventoryWarning === 'no_stock') {
      return '库存为零';
    }
    if (inventoryWarning === 'no_client') {
      return '无客户信息';
    }
    if (inventoryWarning === 'inventory_fetch_failed') {
      return '获取库存失败';
    }
    return `${totalAvailable}${unit}`;
  };

  const getTooltipText = () => {
    if (inventoryWarning === 'insufficient_total') {
      return `库存不足：需要 ${quantity_planned}${unit}，仅有 ${totalAvailable}${unit}`;
    }
    if (inventoryWarning === 'no_inventory') {
      return '该产品没有库存记录';
    }
    if (inventoryWarning === 'no_stock') {
      return '所有位置库存为零';
    }
    if (totalAvailable >= quantity_planned) {
      return `库存充足：可用 ${totalAvailable}${unit}，需要 ${quantity_planned}${unit}`;
    }
    if (totalAvailable > 0) {
      return `库存紧张：可用 ${totalAvailable}${unit}，需要 ${quantity_planned}${unit}`;
    }
    return '库存状态';
  };

  const statusColor = getStatusColor();

  return (
    <div className="flex flex-col gap-1">
      {/* Total Available Badge */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={statusColor} className="gap-1 font-medium cursor-default">
              {getStatusIcon()}
              {getStatusText()}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{getTooltipText()}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Location Distribution */}
      {locationsWithStock > 0 && !inventoryWarning && (
        <div className="flex items-center gap-1 text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span className="text-xs">
            分布在 {locationsWithStock} 个位置
          </span>
        </div>
      )}

      {/* Frozen Stock Info */}
      {frozen_qty > 0 && (
        <div className="flex items-center gap-1 text-blue-600">
          <span className="text-xs font-medium">
            已冻结 {frozen_qty}{unit}
          </span>
        </div>
      )}

      {/* Warning Messages */}
      {inventoryWarning && inventoryWarning !== 'insufficient_total' && (
        <span className="text-xs text-destructive flex items-center gap-1">
          <XCircle className="h-3.5 w-3.5" />
          {inventoryWarning === 'no_inventory' && '无库存记录'}
          {inventoryWarning === 'no_stock' && '库存为零'}
          {inventoryWarning === 'no_client' && '无客户信息'}
          {inventoryWarning === 'inventory_fetch_failed' && '获取失败'}
        </span>
      )}
    </div>
  );
}

export default InventoryStatusDisplay;
